import axios from 'axios'
import { setupMobileInterceptors } from './mobileAuthService.js'
import { USE_JWT_COOKIE } from '../../shared/auth/authConfig.js'

/**
 * APIベースURL
 * モバイルユーザー情報用のAPIエンドポイント
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/pix/api'

/**
 * モバイルユーザー情報用のaxiosインスタンス
 * 共通の設定とインターセプターが適用されたHTTPクライアント
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: USE_JWT_COOKIE,
  headers: {
    'Content-Type': 'application/json',
  },
})

// モバイルユーザー情報用のインターセプターを設定
setupMobileInterceptors(apiClient, 'Mobile User Info')

/**
 * モバイルユーザー情報サービス
 * ユーザー情報の取得、更新、削除、プロフィール画像管理などのユーザー関連機能を提供
 */
export const mobileUserInfoService = {
  /**
   * ユーザー情報の取得
   * 現在ログインしているユーザーの情報を取得
   * @param {string} userId - ログイン時取得したユーザーID
   * @returns {Promise<Object>} ユーザー情報データ
   */
  async getPixUser(userId) {
    if (!userId) {
      throw new Error('ユーザーIDを取得できませんでした。ログインしてください。')
    }

    const response = await apiClient.post('/getPixUserList', {
      pixUserId: userId
    })
    return response.data
  },

  /**
   * メールアドレスの重複チェック
   * 指定されたメールアドレスが既に登録されているかチェック
   * @param {string} mailAddress - チェック対象のメールアドレス
   * @returns {Promise<Object>} 重複チェック結果
   */
  async getPixUserCount(mailAddress) {
    const response = await apiClient.post('/getPixUserCount', {
      mailAddress: mailAddress
    })
    return response.data
  },

  /**
   * ユーザー情報の更新
   * 現在ログインしているユーザーの情報を更新
   * @param {Object} userData - 更新するユーザーデータ
   * @param {string} userData.userName - ユーザー名
   * @param {string} userData.loginPassword - ログインパスワード（オプション）
   * @param {string} userData.mailAddress - メールアドレス
   * @param {string} userData.riyoKengen - 利用権限
   * @param {string} userId - ログイン時取得したユーザーID
   * @returns {Promise<Object>} 更新結果
   */
  async updatePixUser(userData, userId) {
    if (!userId) {
      throw new Error('ユーザーIDを取得できませんでした。ログインしてください。')
    }

    const data = {
      use_init: 'true',           // 初期化フラグ
      action: 'add',              // アクション種別
      pageAction: 'updateInfo',   // ページアクション
      pixUserId: userId,
      userName: userData.userName,
      loginPassword: userData.loginPassword || null,
      mailAddress: userData.mailAddress,
      riyoKengen: userData.riyoKengen
    }
    
    const response = await apiClient.post('/updatePixUser', data)
    return response.data
  },

  /**
   * ユーザーの削除
   * 現在ログインしているユーザーアカウントを削除
   * @param {string} userId - ログイン時取得したユーザーID
   * @returns {Promise<Object>} 削除結果
   */
  async deletePixUser(userId) {
    if (!userId) {
      throw new Error('ユーザーIDを取得できませんでした。ログインしてください。')
    }

    const data = {
      use_init: 'true',    // 初期化フラグ
      pixUserId: userId
    }
    const response = await apiClient.post('/removePixUser', data)
    return response.data
  },

  /**
   * プロフィール画像URLの取得
   * 指定されたプロフィール画像のURLを生成
   * @param {string} pixUserId - PIXユーザーID（オプション、未指定時はJWTトークンから取得）
   * @param {string} profileImageId - プロフィール画像ID
   * @param {string} filenameExtension - ファイル拡張子
   * @param {string} userId - ログイン時取得したユーザーID
   * @returns {string} プロフィール画像URLまたはデフォルト画像パス
   */
  getProfileImageUrl(pixUserId, profileImageId, filenameExtension, userId) {
    if (!profileImageId) {
      return '/noprofileimage.png'
    }
    
    // pixUserIdが渡されていない場合は、userIdを使用
    if (!pixUserId) {
      pixUserId = userId
    }
    
    if (!pixUserId) {
      return '/noprofileimage.png'
    }
    
    return `/pix/api/getProfileImage?pixUserId=${pixUserId}&profileImageId=${profileImageId}&filenameExtension=${filenameExtension}&type=thumb_comp&width=88&height=88`
  },

  /**
   * プロフィール画像をblobとして取得
   * 指定されたプロフィール画像をblob形式で取得し、URLオブジェクトとして返す
   * @param {string} pixUserId - PIXユーザーID（オプション、未指定時はJWTトークンから取得）
   * @param {string} profileImageId - プロフィール画像ID
   * @param {string} filenameExtension - ファイル拡張子
   * @param {string} userId - ログイン時取得したユーザーID
   * @returns {Promise<string>} プロフィール画像URLまたはエラー画像パス
   */
  async getProfileImage(pixUserId, profileImageId, filenameExtension, userId) {
    if (!profileImageId) {
      return '/core/img/errorimage.png'
    }
    
    // pixUserIdが渡されていない場合は、userIdを使用
    if (!pixUserId) {
      pixUserId = userId
    }
    
    if (!pixUserId) {
      return '/core/img/errorimage.png'
    }

    try {
      const response = await apiClient.get('/getProfileImage', {
        params: {
          pixUserId,
          profileImageId,
          filenameExtension,
          type: 'thumb_comp',    // サムネイル圧縮タイプ
          width: 88,             // 幅
          height: 88            // 高さ
        },
        responseType: 'blob',
        headers: { Accept: 'image/jpeg' }
      })
      return URL.createObjectURL(response.data)
    } catch (error) {
      return '/core/img/errorimage.png'
    }
  },
}

export default apiClient
